import { useState } from 'react';
import { Alert, App, Checkbox, Modal, Progress, Space, Typography } from 'antd';
import { REMOVABLE_STATUSES, type EmailStatus } from '@crm/shared';
import { errorMessage } from '../../api/client';
import { STATUS_LABEL, useRemoveBadEmails, type MarkInvalidProgress } from '../../features/verification/api';
import { plural } from '../../utils/format';

const NOTE: Record<string, string> = {
  INVALID: 'Will bounce (failed checks or a hard bounce)',
  DISPOSABLE: 'Throwaway mailbox provider',
  SUPPRESSED: 'Bounced, complained or unsubscribed',
};

/** Strips bad addresses from contacts; the contacts themselves (name, phone, organization, history) stay. */
export function RemoveBadEmailsModal({ open, onClose, counts }: { open: boolean; onClose: () => void; counts: Partial<Record<EmailStatus, number>> }) {
  const { message } = App.useApp();
  const remove = useRemoveBadEmails();
  const [picked, setPicked] = useState<EmailStatus[]>([...REMOVABLE_STATUSES]);
  const [progress, setProgress] = useState<MarkInvalidProgress | null>(null);
  const total = picked.reduce((n, s) => n + (counts[s] ?? 0), 0);

  const close = () => {
    if (remove.isPending) return;
    setProgress(null);
    onClose();
  };
  const run = () =>
    remove
      .mutateAsync({ statuses: picked, apply: true, onProgress: setProgress })
      .then((r) => {
        if (r.failed) message.warning(`Removed ${plural(r.removed, 'email address', 'email addresses')}; ${plural(r.failed, 'contact')} could not be changed`);
        else message.success(`Removed ${plural(r.removed, 'email address', 'email addresses')}`);
        close();
      })
      .catch((e) => message.error(errorMessage(e)));

  return (
    <Modal
      open={open}
      title="Remove bad email addresses"
      onCancel={close}
      onOk={run}
      okText={total ? `Remove ${plural(total, 'email address', 'email addresses')}` : 'Remove'}
      okButtonProps={{ danger: true, disabled: !total, loading: remove.isPending }}
      cancelButtonProps={{ disabled: remove.isPending }}
      closable={!remove.isPending}
      maskClosable={false}
      destroyOnHidden
    >
      <Typography.Paragraph type="secondary">
        Deletes the address from each contact. The contact stays with its name, phone, organization and history; if it has another address, that one becomes the primary and is checked. Removed addresses stay on the suppression list, so a later import can't put them back into a send.
      </Typography.Paragraph>
      <Checkbox.Group value={picked} onChange={(v) => setPicked(v as EmailStatus[])} disabled={remove.isPending} style={{ width: '100%' }}>
        <Space direction="vertical">
          {REMOVABLE_STATUSES.map((s) => (
            <Checkbox key={s} value={s} disabled={!counts[s]}>
              <b>{STATUS_LABEL[s]}</b> ({(counts[s] ?? 0).toLocaleString()}) <Typography.Text type="secondary">· {NOTE[s]}</Typography.Text>
            </Checkbox>
          ))}
        </Space>
      </Checkbox.Group>
      {progress && <Progress style={{ marginTop: 16 }} percent={progress.total ? Math.round((progress.done / progress.total) * 100) : 100} format={() => `${progress.done.toLocaleString()} / ${progress.total.toLocaleString()}`} />}
      {total > 0 && !progress && <Alert type="warning" showIcon style={{ marginTop: 16 }} title="This can't be undone" description="The addresses are deleted from the contacts. Each change is recorded on the contact's timeline." />}
    </Modal>
  );
}
